import React, { useEffect, useMemo, useState } from "react";
import { db } from "../firebase";
import { collection, deleteDoc, doc, onSnapshot, setDoc } from "firebase/firestore";

const emptyAuditForm = {
  auditNo: "",
  auditDate: "",
  auditor: "",
  contractor: "",
  auditType: "Score Audit",
  physicalScorePercentage: "",
  adminScorePercentage: "",
  findings: "",
  status: "Outstanding",
  closeOutDate: "",
  comments: "",
};

function statusStyle(status) {
  if (status === "Outstanding") {
    return {
      background: "#fef2f2",
      color: "#dc2626",
      fontWeight: 700,
    };
  }

  if (status === "Closed Out") {
    return {
      background: "#f0fdf4",
      color: "#16a34a",
      fontWeight: 700,
    };
  }

  return {};
}

function isOverdue(closeOutDate, status) {
  if (!closeOutDate || status === "Closed Out") return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const targetDate = new Date(closeOutDate);
  if (Number.isNaN(targetDate.getTime())) return false;
  targetDate.setHours(0, 0, 0, 0);

  return targetDate < today;
}

function AuditRow({ label, helper, children }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "240px 1fr",
        gap: 16,
        alignItems: "center",
        marginBottom: 14,
      }}
    >
      <div>
        <div style={{ fontWeight: 600 }}>{label}</div>
        {helper && (
          <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
            {helper}
          </div>
        )}
      </div>
      <div>{children}</div>
    </div>
  );
}

export default function HealthSafetyAuditsSection({ currentProjectId }) {
  const [auditForm, setAuditForm] = useState(emptyAuditForm);
  const [audits, setAudits] = useState([]);
  const [editingAuditId, setEditingAuditId] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [auditTypeFilter, setAuditTypeFilter] = useState("All");

  useEffect(() => {
    if (!currentProjectId) {
      setAudits([]);
      return;
    }

    const unsubscribe = onSnapshot(
      collection(db, "projects", currentProjectId, "healthSafetyAudits"),
      (snapshot) => {
        const liveAudits = snapshot.docs.map((item) => ({
          id: item.id,
          ...item.data(),
        }));
        setAudits(liveAudits);
      },
      (error) => {
        console.error("Error loading health and safety audits from Firestore", error);
      }
    );

    return () => unsubscribe();
  }, [currentProjectId]);

  const filteredAudits = useMemo(() => {
    return [...audits]
      .filter((row) => {
        const matchesStatus = statusFilter === "All" || row.status === statusFilter;
        const matchesAuditType = auditTypeFilter === "All" || row.auditType === auditTypeFilter;

        const haystack = [
          row.auditNo,
          row.auditDate,
          row.auditor,
          row.contractor,
          row.auditType,
          row.findings,
          row.comments,
        ]
          .join(" ")
          .toLowerCase();

        const matchesSearch =
          !searchText.trim() || haystack.includes(searchText.toLowerCase());

        return matchesStatus && matchesAuditType && matchesSearch;
      })
      .sort((a, b) => (b.auditDate || "").localeCompare(a.auditDate || ""));
  }, [audits, statusFilter, auditTypeFilter, searchText]);

  const summary = useMemo(() => {
    return {
      totalAudits: audits.length,
      outstandingFindings: audits.filter((item) => item.status === "Outstanding").length,
      overdueFindings: audits.filter((item) => isOverdue(item.closeOutDate, item.status)).length,
      closedFindings: audits.filter((item) => item.status === "Closed Out").length,
    };
  }, [audits]);

  const handleSaveAudit = async () => {
    if (!currentProjectId || !auditForm.auditNo.trim()) return;

    const auditId = editingAuditId || `HSA-${Date.now()}`;
    const payload = {
      ...auditForm,
      auditNo: auditForm.auditNo.trim(),
      auditor: auditForm.auditor.trim(),
      contractor: auditForm.contractor.trim(),
      findings: auditForm.findings.trim(),
      comments: auditForm.comments.trim(),
      id: auditId,
    };

    try {
      await setDoc(
        doc(db, "projects", currentProjectId, "healthSafetyAudits", auditId),
        payload,
        { merge: true }
      );

      setAudits((prev) => {
        const withoutCurrent = prev.filter((item) => item.id !== auditId);
        return [...withoutCurrent, payload].sort((a, b) =>
          (b.auditDate || "").localeCompare(a.auditDate || "")
        );
      });

      setAuditForm(emptyAuditForm);
      setEditingAuditId(null);
    } catch (error) {
      console.error("Error saving health and safety audit to Firestore", error);
    }
  };

  const handleEditAudit = (row) => {
    setAuditForm({
      auditNo: row.auditNo || "",
      auditDate: row.auditDate || "",
      auditor: row.auditor || "",
      contractor: row.contractor || "",
      auditType: row.auditType || "Score Audit",
      physicalScorePercentage: row.physicalScorePercentage || "",
      adminScorePercentage: row.adminScorePercentage || "",
      findings: row.findings || "",
      status: row.status || "Outstanding",
      closeOutDate: row.closeOutDate || "",
      comments: row.comments || "",
    });
    setEditingAuditId(row.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDeleteAudit = async (id) => {
    if (!currentProjectId) return;

    try {
      await deleteDoc(doc(db, "projects", currentProjectId, "healthSafetyAudits", id));
      if (editingAuditId === id) {
        setAuditForm(emptyAuditForm);
        setEditingAuditId(null);
      }
    } catch (error) {
      console.error("Error deleting health and safety audit from Firestore", error);
    }
  };

  return (
    <section className="panel">
      <h2>Health &amp; Safety Audits</h2>

      <div className="card" style={{ marginBottom: 20 }}>
        <h3 style={{ marginTop: 0 }}>Audit Summary</h3>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Total Audits</th>
                <th>Outstanding Findings</th>
                <th>Overdue Close-Outs</th>
                <th>Closed Out Findings</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{summary.totalAudits}</td>
                <td>{summary.outstandingFindings}</td>
                <td>{summary.overdueFindings}</td>
                <td>{summary.closedFindings}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 20 }}>
        <h3 style={{ marginTop: 0 }}>Health &amp; Safety Audit Capture</h3>

        <AuditRow label="Audit Number" helper="Capture the health and safety audit reference number">
          <input
            type="text"
            value={auditForm.auditNo}
            onChange={(e) => setAuditForm({ ...auditForm, auditNo: e.target.value })}
          />
        </AuditRow>

        <AuditRow label="Audit Date" helper="Date of the audit report">
          <input
            type="date"
            value={auditForm.auditDate}
            onChange={(e) => setAuditForm({ ...auditForm, auditDate: e.target.value })}
          />
        </AuditRow>

        <AuditRow label="Auditor" helper="Name of the person issuing the audit report">
          <input
            type="text"
            value={auditForm.auditor}
            onChange={(e) => setAuditForm({ ...auditForm, auditor: e.target.value })}
          />
        </AuditRow>

        <AuditRow label="Contractor" helper="Principal contractor or audited contractor">
          <input
            type="text"
            value={auditForm.contractor}
            onChange={(e) => setAuditForm({ ...auditForm, contractor: e.target.value })}
          />
        </AuditRow>

        <AuditRow label="Audit Type" helper="Choose whether the entry is a scored audit or a photo audit">
          <select
            value={auditForm.auditType}
            onChange={(e) => setAuditForm({ ...auditForm, auditType: e.target.value })}
          >
            <option value="Score Audit">Score Audit</option>
            <option value="Photo Audit">Photo Audit</option>
          </select>
        </AuditRow>

        <AuditRow label="Physical Score %" helper="Physical audit percentage from the report">
          <input
            type="number"
            min="0"
            max="100"
            step="0.01"
            value={auditForm.physicalScorePercentage}
            onChange={(e) =>
              setAuditForm({ ...auditForm, physicalScorePercentage: e.target.value })
            }
          />
        </AuditRow>

        <AuditRow label="Admin Score %" helper="Administrative audit percentage from the report">
          <input
            type="number"
            min="0"
            max="100"
            step="0.01"
            value={auditForm.adminScorePercentage}
            onChange={(e) =>
              setAuditForm({ ...auditForm, adminScorePercentage: e.target.value })
            }
          />
        </AuditRow>

        <AuditRow label="Findings" helper="Capture the findings, contraventions, or photo notes from the report">
          <textarea
            rows="4"
            value={auditForm.findings}
            onChange={(e) => setAuditForm({ ...auditForm, findings: e.target.value })}
          />
        </AuditRow>

        <AuditRow label="Status" helper="Tick whether the finding has been closed out or is still outstanding">
          <label style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <input
              type="checkbox"
              checked={auditForm.status === "Closed Out"}
              onChange={(e) =>
                setAuditForm({
                  ...auditForm,
                  status: e.target.checked ? "Closed Out" : "Outstanding",
                })
              }
            />
            {auditForm.status === "Closed Out" ? "Closed Out" : "Outstanding"}
          </label>
        </AuditRow>

        <AuditRow label="Close-Out Date" helper="Date the finding or report item was closed out">
          <input
            type="date"
            value={auditForm.closeOutDate}
            onChange={(e) => setAuditForm({ ...auditForm, closeOutDate: e.target.value })}
          />
        </AuditRow>

        <AuditRow label="Comments" helper="General notes about the audit report">
          <textarea
            rows="3"
            value={auditForm.comments}
            onChange={(e) => setAuditForm({ ...auditForm, comments: e.target.value })}
          />
        </AuditRow>

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button type="button" onClick={handleSaveAudit}>
            {editingAuditId ? "Update H&amp;S Audit Report" : "Save H&amp;S Audit Report"}
          </button>
          {editingAuditId && (
            <button
              type="button"
              onClick={() => {
                setAuditForm(emptyAuditForm);
                setEditingAuditId(null);
              }}
              style={{ background: "#6b7280" }}
            >
              Clear
            </button>
          )}
        </div>
      </div>

      <div className="card" style={{ marginBottom: 20 }}>
        <h3 style={{ marginTop: 0 }}>Search and Filter</h3>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: 14,
          }}
        >
          <div>
            <label>Search</label>
            <input
              type="text"
              placeholder="Search health and safety audits"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </div>

          <div>
            <label>Status</label>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="All">All</option>
              <option value="Outstanding">Outstanding</option>
              <option value="Closed Out">Closed Out</option>
            </select>
          </div>

          <div>
            <label>Audit Type</label>
            <select value={auditTypeFilter} onChange={(e) => setAuditTypeFilter(e.target.value)}>
              <option value="All">All</option>
              <option value="Score Audit">Score Audit</option>
              <option value="Photo Audit">Photo Audit</option>
            </select>
          </div>
        </div>
      </div>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Health &amp; Safety Audit Register</h3>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Audit No</th>
                <th>Date</th>
                <th>Audit Type</th>
                <th>Auditor</th>
                <th>Contractor</th>
                <th>Physical Score %</th>
                <th>Admin Score %</th>
                <th>Findings</th>
                <th>Close-Out Date</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredAudits.length === 0 ? (
                <tr>
                  <td colSpan="11" style={{ textAlign: "center", color: "#6b7280" }}>
                    No health and safety audits captured.
                  </td>
                </tr>
              ) : (
                filteredAudits.map((row) => (
                  <tr key={row.id}>
                    <td>{row.auditNo}</td>
                    <td>{row.auditDate || "-"}</td>
                    <td>{row.auditType || "-"}</td>
                    <td>{row.auditor || "-"}</td>
                    <td>{row.contractor || "-"}</td>
                    <td>{row.physicalScorePercentage ? `${row.physicalScorePercentage}%` : "-"}</td>
                    <td>{row.adminScorePercentage ? `${row.adminScorePercentage}%` : "-"}</td>
                    <td style={{ maxWidth: 320, whiteSpace: "pre-wrap" }}>{row.findings || "-"}</td>
                    <td
                      style={
                        isOverdue(row.closeOutDate, row.status)
                          ? { background: "#fef2f2", color: "#dc2626", fontWeight: 700 }
                          : {}
                      }
                    >
                      {row.closeOutDate || "-"}
                    </td>
                    <td style={statusStyle(row.status)}>{row.status || "-"}</td>
                    <td>
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        <button
                          onClick={() => handleEditAudit(row)}
                          style={{ background: "#2563eb" }}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteAudit(row.id)}
                          style={{ background: "#dc2626" }}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}